import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import Tooltip from '../components/ui/Tooltip';
import { recommendedFor } from './rolePresets';
import { isLastVisible, railLeaderOf, withFollowers } from './railIds';

/**
 * The grouped tool list: "Recommended for a <role>" first, then "Other tools".
 * Shared by the onboarding Tools step and Settings → Appearance → Your tools.
 *
 * Each row is ONE switch (the whole row toggles, Space/Enter included); the
 * toggle on the right is the Settings toggle's look, drawn as a span because a
 * button inside a switch would be a second, nested control. A row whose tool
 * is the last one still visible cannot be switched off, and a follower
 * (History) cannot come on while its leader (API Client) is off.
 *
 * Rows are keyed by tool id and the list never changes shape while toggling,
 * so a toggle is an in-place attribute change — nothing re-mounts.
 */
export default function ToolList({ roleId, tools, hidden, onToggle }) {
  const { t } = useTranslation();
  const hiddenSet = withFollowers(hidden);
  const availableIds = tools.map((tool) => tool.id);
  const custom = !roleId || roleId === 'custom';
  const recommended = new Set(recommendedFor(custom ? 'custom' : roleId));
  const primary = custom ? tools : tools.filter((tool) => recommended.has(tool.id));
  const other = custom ? [] : tools.filter((tool) => !recommended.has(tool.id));

  const row = (tool) => {
    const on = !hiddenSet.has(tool.id);
    const leader = railLeaderOf(tool.id);
    const following = Boolean(leader) && hiddenSet.has(leader);
    const last = on && isLastVisible(hidden, tool.id, availableIds);
    const locked = last || following;
    const name = t(`onboarding.tool.${tool.key}.name`);
    const leaderTool = following ? tools.find((x) => x.id === leader) : null;
    const lockNote = following
      ? t('onboarding.tools.follows', { name: leaderTool ? t(`onboarding.tool.${leaderTool.key}.name`) : leader })
      : t('onboarding.tools.lastOne');
    const toggle = () => {
      if (!locked) onToggle(tool.id);
    };
    const control = (
      <span
        className={`settings-toggle ob-trow-toggle${on ? ' settings-toggle--on' : ''}${locked ? ' settings-toggle--disabled' : ''}`}
        aria-hidden="true"
      >
        <span className="settings-toggle-thumb" />
      </span>
    );
    return (
      <div
        key={tool.id}
        className={`ob-trow${on ? '' : ' ob-trow--off'}`}
        role="switch"
        aria-checked={on}
        aria-disabled={locked || undefined}
        aria-label={name}
        data-tool={tool.id}
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span className="ob-trow-ic">
          <tool.Icon size={15} />
        </span>
        <span className="ob-trow-name">{name}</span>
        <span className="ob-trow-desc">{t(`onboarding.tool.${tool.key}.desc`)}</span>
        {locked ? <Tooltip content={lockNote} side="left">{control}</Tooltip> : control}
      </div>
    );
  };

  return (
    <div className="ob-tlist">
      <div className="ob-tlist-head">
        <Sparkles size={12} aria-hidden="true" />
        {t(`onboarding.roles.${custom ? 'custom' : roleId}.recommended`)}
        <span className="ob-tlist-n">{primary.length}</span>
      </div>
      {primary.map(row)}
      {other.length > 0 && (
        <>
          <div className="ob-tlist-head">
            {t('onboarding.tools.other')}
            <span className="ob-tlist-n">{t('onboarding.tools.otherNote')}</span>
          </div>
          {other.map(row)}
        </>
      )}
    </div>
  );
}
